<script lang="ts">
	import { appStore } from '../stores/appStore';
	import type { AppApiClient } from '../api_contracts/app.client';
	import Preloader from '../components/auth/Preloader.svelte';
	import DarkModeToggle from '../components/auth/DarkModeToggle.svelte';
	import BrandingSide from '../components/auth/BrandingSide.svelte';
	import { onMount } from 'svelte';

	let client: AppApiClient | null = $state(null);
	let email = $state('');
	let verificationCode = $state('');
	let loading = $state(false);
	let error = $state('');
	let success = $state(false);
	let resendCooldown = $state(0);
	let verifyingToken = $state(false);

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	// Check for token in URL (from email link)
	onMount(() => {
		const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
		const token = urlParams.get('token');

		if (token && client) {
			verifyByToken(token);
		}

		// Get email from localStorage if available (from registration)
		const registeredEmail = localStorage.getItem('pendingVerificationEmail');
		if (registeredEmail) {
			email = registeredEmail;
		}
	});

	async function verifyByToken(token: string) {
		if (!client) return;

		try {
			verifyingToken = true;
			error = '';

			const result = await client.verifyEmailByToken({ token });

			if (result.response.successFull) {
				success = true;
				localStorage.removeItem('pendingVerificationEmail');
				// Redirect to login after 2 seconds
				setTimeout(() => {
					window.location.hash = '/login';
				}, 2000);
			} else {
				error = result.response.reason || 'Invalid or expired verification link';
			}
		} catch (e: any) {
			error = e.message || 'Error verifying email';
			console.error('Verification error:', e);
		} finally {
			verifyingToken = false;
		}
	}

	async function handleVerify(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		if (!email || !verificationCode) {
			error = 'Please enter both email and verification code';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.verifyEmail({
				email,
				code: verificationCode
			});

			if (result.response.successFull) {
				success = true;
				localStorage.removeItem('pendingVerificationEmail');
				// Redirect to login after 2 seconds
				setTimeout(() => {
					window.location.hash = '/login';
				}, 2000);
			} else {
				error = result.response.reason || 'Verification failed';
			}
		} catch (e: any) {
			error = e.message || 'Error during verification';
			console.error('Verification error:', e);
		} finally {
			loading = false;
		}
	}

	async function handleResend() {
		if (!client || !email) {
			error = 'Please enter your email address';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.resendVerificationEmail({ email });

			if (result.response.successFull) {
				// Start cooldown (60 seconds)
				resendCooldown = 60;
				const interval = setInterval(() => {
					resendCooldown--;
					if (resendCooldown <= 0) {
						clearInterval(interval);
					}
				}, 1000);

				// Show success message
				alert('Verification email sent! Please check your inbox.');
			} else {
				error = result.response.reason || 'Failed to resend verification email';
			}
		} catch (e: any) {
			error = e.message || 'Error resending verification email';
			console.error('Resend error:', e);
		} finally {
			loading = false;
		}
	}
</script>

<Preloader />
<DarkModeToggle />

<div class="relative z-1 bg-white p-6 sm:p-0 dark:bg-gray-900">
	<div class="flex h-screen w-full flex-col justify-center sm:p-0 lg:flex-row dark:bg-gray-900">
		<!-- Left Side: Form -->
		<div class="flex w-full flex-1 flex-col lg:w-1/2">
			<div class="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center">
				{#if verifyingToken}
					<!-- Loading state for token verification -->
					<div class="text-center py-8">
						<div class="w-16 h-16 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<h2 class="text-2xl font-semibold text-gray-900 mb-2 dark:text-white/90">
							Verifying your email...
						</h2>
					</div>
				{:else if success}
					<!-- Success State -->
					<div class="text-center py-8">
						<svg
							class="w-16 h-16 text-green-500 mx-auto mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<h2 class="text-2xl font-semibold text-gray-900 mb-2 dark:text-white/90">
							Email verified successfully!
						</h2>
						<p class="text-sm text-gray-500 dark:text-gray-400">Redirecting to login...</p>
					</div>
				{:else}
					<!-- Verification Form -->
					<div class="mb-5 sm:mb-8">
						<div class="flex items-center justify-center mb-4">
							<svg
								class="w-16 h-16 text-brand-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
								/>
							</svg>
						</div>
						<h1 class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 text-center dark:text-white/90">
							Verify Your Email
						</h1>
						<p class="text-sm text-gray-500 text-center dark:text-gray-400">
							Enter the 6-digit code we sent to your email
						</p>
					</div>

					<div>
						<form onsubmit={handleVerify}>
							{#if error}
								<div class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<!-- Email -->
								<div>
									<label for="email" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
										Email<span class="text-error-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										bind:value={email}
										disabled={loading}
										placeholder="your@email.com"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Verification Code -->
								<div>
									<label for="code" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
										Verification Code<span class="text-error-500">*</span>
									</label>
									<input
										type="text"
										id="code"
										bind:value={verificationCode}
										disabled={loading}
										maxlength="6"
										inputmode="numeric"
										placeholder="000000"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 text-center text-2xl tracking-widest"
										required
										autofocus
									/>
								</div>

								<!-- Verify Button -->
								<div>
									<button
										type="submit"
										disabled={loading}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Verifying...' : 'Verify Email'}
									</button>
								</div>

								<!-- Resend Link -->
								<div class="text-center">
									<p class="text-sm text-gray-600 dark:text-gray-400">
										Didn't receive the code?
										{#if resendCooldown > 0}
											<span class="text-gray-500">Resend in {resendCooldown}s</span>
										{:else}
											<button
												type="button"
												onclick={handleResend}
												disabled={loading}
												class="text-brand-500 hover:text-brand-600 dark:text-brand-400 font-medium"
											>
												Resend verification email
											</button>
										{/if}
									</p>
								</div>

								<!-- Back to Login -->
								<div class="text-center">
									<a
										href="#/login"
										class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
									>
										Back to login
									</a>
								</div>
							</div>
						</form>
					</div>
				{/if}
			</div>
		</div>

		<!-- Right Side: Branding -->
		<BrandingSide />
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
	}
</style>
